import styled from "styled-components";
import React, { useEffect, useContext, useState, useMemo } from "react";
import { IoCloseOutline, IoAddCircleOutline, IoTrashOutline, IoReorderThreeOutline } from "react-icons/io5";
import { updateItens } from "../../services/api";
import { AuthContext } from "../../contexts/auth";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const Overlay = styled.div`
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
`;

const ModalContainer = styled.div`
  z-index: 1001;
  position: relative;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 95vh;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 28px 32px 20px;
  border-bottom: 1px solid #e2e8f0;

  .header-text h2 {
    font-size: 22px;
    font-weight: 700;
    color: #2d3748;
    margin: 0 0 4px;
  }

  .header-text p {
    font-size: 14px;
    color: #718096;
    margin: 0;
  }

  .header-text .item-name {
    display: inline-block;
    margin-top: 8px;
    padding: 4px 12px;
    background: linear-gradient(135deg, rgba(49, 140, 213, 0.1), rgba(79, 172, 254, 0.1));
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: rgb(49, 140, 213);
  }

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 8px;
    color: #a0aec0;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #f7fafc;
      color: #4a5568;
    }
  }

  @media (max-width: 480px) {
    padding: 20px 16px 16px;
  }
`;

const Body = styled.div`
  padding: 24px 32px;
  flex: 1;
  overflow-y: auto;

  .section-label {
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
  }

  .linked-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
  }

  .linked-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    transition: all 0.2s;

    &:hover {
      border-color: #cbd5e0;
      background: #edf2f7;
    }

    .grupo-info {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .grupo-name {
        font-size: 14px;
        font-weight: 600;
        color: #2d3748;
      }

      .grupo-meta {
        font-size: 12px;
        color: #718096;
        display: flex;
        gap: 8px;
        align-items: center;

        .badge {
          display: inline-block;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
        }

        .badge-mandatory {
          background: #fed7d7;
          color: #c53030;
        }

        .badge-optional {
          background: #c6f6d5;
          color: #276749;
        }

        .badge-type {
          background: #e9d8fd;
          color: #6b46c1;
        }
      }
    }

    .remove-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px 10px;
      border-radius: 8px;
      color: #e53e3e;
      font-size: 13px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: all 0.2s;

      &:hover {
        background: #fff5f5;
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 24px;
    color: #a0aec0;
    font-size: 14px;
    background: #f7fafc;
    border: 1px dashed #e2e8f0;
    border-radius: 10px;
    margin-bottom: 24px;
  }

  .add-section {
    display: flex;
    gap: 8px;
    align-items: center;

    select {
      flex: 1;
      padding: 10px 14px;
      border: 1.5px solid #e2e8f0;
      border-radius: 10px;
      font-size: 14px;
      color: #4a5568;
      background: white;
      cursor: pointer;
      transition: border-color 0.2s;
      outline: none;

      &:focus {
        border-color: rgb(49, 140, 213);
        box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.15);
      }

      &:disabled {
        background: #f7fafc;
        cursor: not-allowed;
        color: #a0aec0;
      }
    }

    .add-btn {
      background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
      border: none;
      cursor: pointer;
      padding: 10px 16px;
      border-radius: 10px;
      color: white;
      font-size: 14px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 6px;
      white-space: nowrap;
      transition: all 0.2s;

      &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }
    }
  }

  @media (max-width: 480px) {
    padding: 16px;

    .add-section {
      flex-direction: column;

      select {
        width: 100%;
      }

      .add-btn {
        width: 100%;
        justify-content: center;
      }
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 32px;
  border-top: 1px solid #e2e8f0;

  button {
    padding: 10px 24px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .cancel-btn {
    background: white;
    border: 1.5px solid #e2e8f0;
    color: #4a5568;

    &:hover {
      background: #f7fafc;
      border-color: #cbd5e0;
    }
  }

  .save-btn {
    background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
    border: none;
    color: white;

    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

/**
 * ModalManageItemAdicionais - Gerencia quais grupos de adicionais estao vinculados a um item
 *
 * @param {boolean} isOpen - Controla visibilidade do modal
 * @param {function} onClose - Callback ao fechar
 * @param {function} onSuccess - Callback apos salvar com sucesso
 * @param {object} item - Item sendo editado (com _id, title, adicionais[], etc.)
 * @param {array} availableGrupos - Todos os grupos de adicionais da empresa
 * @param {string|null} filterType - null = todos, "pizza" = so sabores, "simples" = so adicionais
 */
const ModalManageItemAdicionais = ({
  isOpen,
  onClose,
  onSuccess,
  item,
  availableGrupos = [],
  filterType = null,
}) => {
  const { t } = useTranslation("categorias");
  const { user } = useContext(AuthContext);
  const empresa = localStorage.getItem("empresa");
  const empresaParse = JSON.parse(empresa || "{}");
  const idEmpresa = empresaParse.id_empresa;
  const userID = user?._id || user?.id;

  const [linkedGrupos, setLinkedGrupos] = useState([]);
  const [selectedGrupo, setSelectedGrupo] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Sync linkedGrupos when item changes or modal opens
  useEffect(() => {
    if (isOpen && item) {
      setLinkedGrupos(item.adicionais || []);
      setSelectedGrupo("");
    }
  }, [isOpen, item]);

  // Filter available grupos based on filterType and already linked
  const filteredAvailableGrupos = useMemo(() => {
    if (!availableGrupos) return [];

    let filtered = availableGrupos;

    // Apply type filter
    if (filterType === "pizza") {
      filtered = filtered.filter((g) => g.type === "pizza");
    } else if (filterType === "simples") {
      filtered = filtered.filter((g) => !g.type || g.type === "simples");
    }

    // Exclude already linked grupos
    const linkedIds = new Set(linkedGrupos.map((lg) => lg.value));
    filtered = filtered.filter((g) => !linkedIds.has(g._id));

    return filtered;
  }, [availableGrupos, filterType, linkedGrupos]);

  // Find grupo details from availableGrupos by id
  const getGrupoDetails = (grupoId) => {
    return availableGrupos?.find((g) => g._id === grupoId) || null;
  };

  const handleAddGrupo = () => {
    if (!selectedGrupo) return;

    const grupo = availableGrupos.find((g) => g._id === selectedGrupo);
    if (!grupo) return;

    setLinkedGrupos((prev) => [
      ...prev,
      { value: grupo._id, label: grupo.title },
    ]);
    setSelectedGrupo("");
  };

  const handleRemoveGrupo = (grupoValue) => {
    setLinkedGrupos((prev) => prev.filter((g) => g.value !== grupoValue));
  };

  // Reordenar os grupos vinculados ao item via drag-and-drop (Tipo A).
  // A nova ordem é persistida ao clicar em Salvar (updateItens salva linkedGrupos na ordem atual).
  const handleDragEndGrupo = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    setLinkedGrupos((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(source.index, 1);
      arr.splice(destination.index, 0, moved);
      return arr;
    });
  };

  const handleSave = async () => {
    if (!item || isSaving) return;
    setIsSaving(true);

    try {
      const response = await updateItens(
        userID,
        item._id,
        item.id_item,
        idEmpresa,
        item.order,
        item.category_item_id,
        item.category_item_title,
        item.title,
        item.description,
        item.out,
        null, // images - don't change
        item.price,
        linkedGrupos.length > 0, // has_adicional
        linkedGrupos, // updated adicionais
        item.type
      );

      if (response.status === 200) {
        toast(
          response.data.msg || t("manageAdicionaisModal.saveSuccess"),
          { autoClose: 3000, type: "success" }
        );
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast(
          response.data.msg || t("manageAdicionaisModal.saveError"),
          { autoClose: 3000, type: "error" }
        );
      }
    } catch (err) {
      const msg =
        err.response?.data?.msg || t("manageAdicionaisModal.saveError");
      toast(msg, { autoClose: 3000, type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isFlavors = filterType === "pizza";
  const modalTitle = isFlavors
    ? t("manageAdicionaisModal.titleFlavors")
    : t("manageAdicionaisModal.title");
  const modalSubtitle = isFlavors
    ? t("manageAdicionaisModal.subtitleFlavors")
    : t("manageAdicionaisModal.subtitle");

  if (!item) return null;

  return (
    <Overlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer>
        <Header>
          <div className="header-text">
            <h2>{modalTitle}</h2>
            <p>{modalSubtitle}</p>
            <span className="item-name">
              {t("manageAdicionaisModal.itemLabel")}: {item.title}
            </span>
          </div>
          <button className="close-btn" onClick={onClose}>
            <IoCloseOutline size={24} />
          </button>
        </Header>

        <Body>
          <div className="section-label">
            {t("manageAdicionaisModal.linkedGroups")}
          </div>

          {linkedGrupos.length === 0 ? (
            <div className="empty-state">
              {t("manageAdicionaisModal.noLinkedGroups")}
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEndGrupo}>
              <Droppable droppableId="linked-grupos">
                {(dropProvided) => (
                  <div className="linked-list" ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                    {linkedGrupos.map((grupo, index) => {
                      const details = getGrupoDetails(grupo.value);
                      return (
                        <Draggable key={grupo.value} draggableId={String(grupo.value)} index={index}>
                          {(dragProvided) => (
                            <div className="linked-item" ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <span {...dragProvided.dragHandleProps} title={t('order.dragToReorder', { defaultValue: 'Arraste para reordenar' })} style={{ cursor: 'grab', color: '#a0aec0', display: 'flex', alignItems: 'center' }}>
                                  <IoReorderThreeOutline size={20} />
                                </span>
                                <div className="grupo-info">
                      <span className="grupo-name">
                        {grupo.label || details?.title || grupo.value}
                      </span>
                      {details && (
                        <div className="grupo-meta">
                          <span
                            className={`badge ${
                              details.mandatory
                                ? "badge-mandatory"
                                : "badge-optional"
                            }`}
                          >
                            {details.mandatory
                              ? t("mandatory")
                              : t("optional")}
                          </span>
                          {details.type === "pizza" && (
                            <span className="badge badge-type">
                              {t("editGroupModal.pizzaFlavor")}
                            </span>
                          )}
                          <span>
                            {t("selectableRange", {
                              min: details.min,
                              max: details.max,
                            })}
                          </span>
                        </div>
                      )}
                                </div>
                              </div>
                              <button
                                className="remove-btn"
                                onClick={() => handleRemoveGrupo(grupo.value)}
                              >
                                <IoTrashOutline size={16} />
                                {t("manageAdicionaisModal.remove")}
                              </button>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          <div className="section-label">
            {t("manageAdicionaisModal.addGroup")}
          </div>

          <div className="add-section">
            <select
              value={selectedGrupo}
              onChange={(e) => setSelectedGrupo(e.target.value)}
              disabled={filteredAvailableGrupos.length === 0}
            >
              <option value="">
                {filteredAvailableGrupos.length === 0
                  ? t("manageAdicionaisModal.noAvailableGroups")
                  : t("manageAdicionaisModal.selectGroupPlaceholder")}
              </option>
              {filteredAvailableGrupos.map((grupo) => (
                <option key={grupo._id} value={grupo._id}>
                  {grupo.title}
                  {grupo.mandatory ? ` (${t("mandatory")})` : ""}
                  {grupo.type === "pizza"
                    ? ` - ${t("editGroupModal.pizzaFlavor")}`
                    : ""}
                </option>
              ))}
            </select>
            <button
              className="add-btn"
              onClick={handleAddGrupo}
              disabled={!selectedGrupo}
            >
              <IoAddCircleOutline size={18} />
              {t("manageAdicionaisModal.addGroup")}
            </button>
          </div>
        </Body>

        <Footer>
          <button className="cancel-btn" onClick={onClose}>
            {t("manageAdicionaisModal.cancel")}
          </button>
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving
              ? t("loading")
              : t("manageAdicionaisModal.save")}
          </button>
        </Footer>
      </ModalContainer>
    </Overlay>
  );
};

export default ModalManageItemAdicionais;
